// Size chooser drawer: "Standard size" (off-the-rack sizes turned into measurement blueprints via
// assets/simple-size.js) or "Custom size" (starts the MCC measurement survey).
// "Find your size" asks the survey's first step and matches it to a standard size (SimpleSize.find); when it
// can't, the customer goes on to the survey with those answers already filled in.
// Customers with no measurements yet start on a screen that asks which of these they want.
if (!customElements.get('size-chooser')) {
  class SizeChooser extends HTMLElement {
    connectedCallback() {
      if (this._initialised) return;
      this._initialised = true;

      const configEl = this.querySelector('[data-size-chooser-config]');
      try {
        this.config = JSON.parse(configEl?.textContent || '{}');
      } catch (error) {
        console.error('Size chooser config is not valid JSON', error);
        this.config = { groups: {}, product_types: [], default: { mode: 'suit', groups: [] } };
      }

      this.groupsEl = this.querySelector('[data-size-chooser-groups]');
      this.findGroupsEl = this.querySelector('[data-size-chooser-find-groups]');
      this.findButton = this.querySelector('[data-size-chooser-find-submit]');
      this.matchEl = this.querySelector('[data-size-chooser-match]');
      this.saveButton = this.querySelector('[data-size-chooser-save]');
      this.clearButton = this.querySelector('[data-size-chooser-clear]');
      this.tabsEl = this.querySelector('[data-size-chooser-tabs]');
      this.choiceEl = this.querySelector('[data-size-chooser-choice]');
      this.errorEl = this.querySelector('[data-size-chooser-error]');
      this.statusEl = this.querySelector('[data-size-chooser-status]');
      this.loginEl = this.querySelector('[data-size-chooser-login]');
      this.footer = this.querySelector('[data-size-chooser-footer]');
      this.pickerStyle = this.dataset.pickerStyle === 'dropdown' ? 'dropdown' : 'buttons';

      this.querySelectorAll('[data-size-chooser-close]').forEach(el => el.addEventListener('click', () => this.close()));
      this.querySelectorAll('[data-size-chooser-tab], [data-size-chooser-choose]').forEach(button => {
        button.addEventListener('click', () => {
          if (this.busy) return;
          const choice = button.dataset.sizeChooserTab || button.dataset.sizeChooserChoose;
          if (choice === 'custom') this.startCustom();
          else if (choice === 'find') this.showFind();
          else this.showStep('standard');
        });
      });
      this.querySelector('[data-size-chooser-back]')?.addEventListener('click', () => {
        if (!this.busy) this.showStep(this.findReturnStep || 'standard');
      });
      this.findButton?.addEventListener('click', () => this.findSize());
      this.saveButton.addEventListener('click', () => this.save());
      this.clearButton.addEventListener('click', () => this.clear());
      // On the document so Escape works wherever focus is, not only inside the drawer
      document.addEventListener('keyup', event => {
        if (event.key === 'Escape' && this.classList.contains('is-visible') && !this.busy) this.close();
      });
    }

    // ----- config helpers -----
    ruleForType(productType) {
      const type = (productType || '').toLowerCase();
      return (this.config.product_types || []).find(rule => (rule.match || []).some(word => type.includes(word))) || this.config.default || { mode: 'suit', groups: [] };
    }

    modeForType(productType) {
      return this.ruleForType(productType).mode;
    }

    modesForTypes(productTypes) {
      return [...new Set((productTypes && productTypes.length ? productTypes : [null]).map(type => this.modeForType(type)))];
    }

    // Union of groups for several product types, in config order
    groupsForTypes(productTypes) {
      const wanted = new Set();
      (productTypes && productTypes.length ? productTypes : [null]).forEach(type => {
        (this.ruleForType(type).groups || []).forEach(key => wanted.add(key));
      });
      const dropdowns = this.pickerStyle === 'dropdown';
      return Object.keys(this.config.groups)
        .filter(key => wanted.has(key))
        .map(key => ({ key, ...this.config.groups[key] }))
        .map(group => (dropdowns && group.source && !group.type ? { ...group, type: 'select' } : group));
    }

    // ----- A/B tracking -----
    // The picker style goes on the cart as a hidden attribute, so every order records which version the
    // customer saw, and drawer steps are published as custom events for a Customer Events pixel to forward.
    track(event, data = {}) {
      try {
        window.Shopify?.analytics?.publish?.(`size_chooser_${event}`, { picker_style: this.pickerStyle, ...data });
      } catch (error) {
        console.warn('Size chooser tracking', error);
      }
    }

    recordPickerStyle() {
      if (this.recordedStyle) return;
      this.recordedStyle = true;
      fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { _size_picker: this.pickerStyle } })
      }).catch(error => {
        this.recordedStyle = false;
        console.warn('Size chooser tracking', error);
      });
    }

    // Option values for a group: the app's block tables when available, else the config fallback
    optionsFor(group) {
      const simple = window.SimpleSize;
      let values = [];
      if (simple && group.source === 'height') values = simple.heights();
      else if (simple && group.source) values = simple.sizesFor(group.source);
      if (!values.length) values = group.options || [];
      // Images live in the MCC app's assets; the app embed sets their base URL
      const imageBase = window.__SHOPIFY_ASSET_URL__;
      return values.map(value => ({
        value: String(value),
        label: (group.labels && group.labels[value]) || String(value),
        hint: (group.hints && group.hints[value]) || '',
        image: imageBase && group.images && group.images[value] ? imageBase + group.images[value] : ''
      }));
    }

    // ----- open / close -----
    /**
     * @param {Object} options
     * @param {string[]} options.productTypes  product types to show size groups for
     * @param {Object}   [options.selected]    preselected values keyed by group key
     * @param {Function} options.onStandard    called with ({ selection, modes }) when saved; may return a promise
     * @param {Function} [options.onClear]     called with ({ modes }) when the customer clears saved sizes
     * @param {Function} options.onCustom      called when the customer chooses the measurement survey
     * @param {string}   [options.step]        step to open on instead of the default
     * @param {boolean}  [options.instant]     show without the slide-in (switching back from the survey)
     */
    open(options = {}) {
      this.removeSurveyHeader();
      this.callbacks = options;
      this.productTypes = options.productTypes || [];
      // Switching back from the survey keeps the element focus returns to when the drawer finally closes
      if (!options.instant) this.activeElement = document.activeElement;
      this.renderGroups(this.productTypes, options.selected || {});
      this.renderFindGroups();
      this.showMatch('');
      const hasMeasurements = (Array.isArray(window.blueprints) && window.blueprints.length > 0) || Object.keys(options.selected || {}).length > 0;
      this.showStep(options.step || (hasMeasurements ? 'standard' : 'choice'));
      this.errorEl.hidden = true;
      this.setStatus('');
      this.showLogin(false);
      this.setBusy(null);
      this.hidden = false;
      document.body.style.overflow = 'hidden';
      this.classList.toggle('is-instant', Boolean(options.instant));
      void this.offsetWidth; // force a reflow so the slide-in transition runs
      this.classList.add('is-visible');
      this.querySelector('.size-chooser__panel').focus();
      if (options.instant) requestAnimationFrame(() => this.classList.remove('is-instant'));
      else {
        this.recordPickerStyle();
        this.track('opened', { step: this.step });
      }
    }

    // `instant` hides the drawer without the slide-out, for handing over to the survey
    close({ instant = false } = {}) {
      if (this.busy) return;
      if (this.openDropdownEl) this.closeDropdown(this.openDropdownEl);
      clearTimeout(this.hideTimer);
      this.classList.remove('is-visible');
      document.body.style.overflow = '';
      if (instant) {
        this.hidden = true;
        return;
      }
      this.hideTimer = setTimeout(() => {
        this.hidden = true;
      }, 300);
      this.activeElement?.focus?.();
    }

    // 'choice' (standard / custom, for customers with no measurements yet), 'standard' (the size form)
    // or 'find' (survey step 1 questions)
    showStep(name) {
      if (this.openDropdownEl) this.closeDropdown(this.openDropdownEl);
      const choosing = name === 'choice';
      this.step = name;
      this.choiceEl.hidden = !choosing;
      this.tabsEl.hidden = choosing || name === 'find';
      this.querySelectorAll('[data-size-chooser-tab]').forEach(tab => {
        const active = tab.dataset.sizeChooserTab === name;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      this.querySelectorAll('[data-size-chooser-pane]').forEach(pane => {
        pane.hidden = pane.dataset.sizeChooserPane !== name;
      });
      this.footer.hidden = name === 'choice';
      this.querySelectorAll('[data-size-chooser-actions]').forEach(actions => {
        actions.hidden = actions.dataset.sizeChooserActions !== name;
      });
      this.errorEl.hidden = true;
      this.setStatus('');
      this.querySelector('.size-chooser__body').scrollTop = 0;
    }

    showFind() {
      if (this.step !== 'find') this.findReturnStep = this.step === 'choice' ? 'choice' : 'standard';
      this.showStep('find');
    }

    showMatch(message) {
      if (!this.matchEl) return;
      this.matchEl.textContent = message || '';
      this.matchEl.hidden = !message;
    }

    // Custom size has no pane of its own: it goes straight to the measurement survey.
    // `answers` (from Find your size) are filled into the survey's first step once it shows.
    startCustom(answers = null) {
      this.track('custom_started', { from_find: Boolean(answers) });
      this.close({ instant: true });
      this.callbacks?.onCustom?.({ answers });
      if (answers) this.prefillSurvey(answers);
      this.addSurveyHeader();
    }

    // The survey is the app's own drawer (#cart-custom-modal), which the app rebuilds on every open and which
    // theme and Qikify code look up by id, so it can't be moved in here. Instead it gets a copy of this drawer's
    // title and tabs with "Custom size" active, and the two drawers swap without sliding, so they read as one.
    // The header is removed when the app's drawer closes.
    addSurveyHeader() {
      const started = Date.now();
      const poll = () => {
        const modal = document.getElementById('cart-custom-modal');
        const content = modal?.querySelector('.cart-modal-content');
        if (!content || !modal.classList.contains('visible')) {
          if (Date.now() - started < 6000) setTimeout(poll, 200);
          return;
        }
        this.removeSurveyHeader();
        const closeSurvey = () => {
          this.removeSurveyHeader();
          if (typeof window.closeModal === 'function') window.closeModal();
          else modal.classList.remove('visible');
        };

        const header = document.createElement('div');
        header.className = 'size-chooser__survey-head';
        const head = this.querySelector('.size-chooser__head').cloneNode(true);
        head.querySelector('[id]')?.removeAttribute('id');
        head.querySelector('[data-size-chooser-close]').addEventListener('click', () => {
          closeSurvey();
          this.activeElement?.focus?.();
        });
        const tabs = this.tabsEl.cloneNode(true);
        tabs.hidden = false;
        tabs.querySelectorAll('[data-size-chooser-tab]').forEach(tab => {
          const custom = tab.dataset.sizeChooserTab === 'custom';
          tab.classList.toggle('is-active', custom);
          tab.setAttribute('aria-selected', custom ? 'true' : 'false');
          if (!custom) {
            tab.addEventListener('click', () => {
              closeSurvey();
              this.open({ ...this.callbacks, step: 'standard', instant: true });
            });
          }
        });
        header.append(head, tabs);
        content.prepend(header);
        this.surveyHeader = header;
        // The app's survey ignores Escape; the size chooser closes on it, so the survey does too
        this.surveyKeyup = event => {
          if (event.key === 'Escape') closeSurvey();
        };
        document.addEventListener('keyup', this.surveyKeyup);
        this.surveyObserver = new MutationObserver(() => {
          if (!modal.classList.contains('visible') || modal.style.display === 'none') this.removeSurveyHeader();
        });
        this.surveyObserver.observe(modal, { attributes: true, attributeFilter: ['class', 'style'] });
      };
      poll();
    }

    removeSurveyHeader() {
      this.surveyObserver?.disconnect();
      this.surveyObserver = null;
      this.surveyHeader?.remove();
      this.surveyHeader = null;
      document.removeEventListener('keyup', this.surveyKeyup);
    }

    setBusy(button) {
      this.busy = Boolean(button);
      this.classList.toggle('is-busy', this.busy);
      [this.saveButton, this.clearButton, this.findButton].filter(Boolean).forEach(el => {
        el.classList.toggle('is-loading', el === button);
        el.setAttribute('aria-busy', el === button ? 'true' : 'false');
      });
    }

    setStatus(message) {
      if (this.statusEl) this.statusEl.textContent = message || '';
    }

    showLogin(show) {
      if (!this.loginEl) return;
      this.loginEl.hidden = !show;
      if (show) {
        const link = this.loginEl.querySelector('[data-size-chooser-login-link]');
        const base = this.dataset.loginUrl || '/account/login';
        if (link) link.href = `${base}?return_url=${encodeURIComponent(window.location.pathname + window.location.search)}`;
      }
    }

    // ----- rendering -----
    renderGroups(productTypes, selected) {
      if (this.openDropdownEl) this.closeDropdown(this.openDropdownEl);
      this.currentGroups = this.groupsForTypes(productTypes);
      this.renderGroupList(this.groupsEl, this.currentGroups, selected, '');
    }

    // Find your size questions; answers already given are kept when the drawer is reopened
    renderFindGroups() {
      if (!this.findGroupsEl) return;
      const previous = this.findGroupsEl.childElementCount ? this.getSelection(this.findGroupsEl).selection : {};
      this.renderGroupList(this.findGroupsEl, this.config.find || [], previous, 'Find-');
      this.findGroupsEl.querySelectorAll('[data-of]').forEach(wrapper => this.updateBackupOptions(wrapper));
    }

    renderGroupList(container, groups, selected, idPrefix) {
      container.innerHTML = '';
      groups.forEach(group => {
        const wrapper = document.createElement('div');
        wrapper.className = 'size-chooser__group';
        wrapper.dataset.key = group.key;

        const label = document.createElement('p');
        label.className = 'size-chooser__group-label';
        label.innerHTML = `<span>${group.label}</span><span class="size-chooser__group-selected" data-selected></span>`;
        wrapper.appendChild(label);

        if (group.description) {
          const description = document.createElement('p');
          description.className = 'size-chooser__group-description';
          description.textContent = group.description;
          wrapper.appendChild(description);
        }

        const options = this.optionsFor(group);
        const current = selected[group.key] != null ? String(selected[group.key]) : (group.default || '');

        if (group.type === 'select') {
          wrapper.appendChild(this.renderDropdown(group, options, current, idPrefix));
        } else if (group.type === 'number') {
          const input = document.createElement('input');
          input.type = 'number';
          input.inputMode = 'numeric';
          input.className = 'size-chooser__input';
          input.id = `SizeChooser-${idPrefix}${group.key}`;
          input.min = group.min;
          input.max = group.max;
          input.placeholder = group.placeholder || '';
          input.setAttribute('aria-label', group.question || group.label);
          input.value = current;
          input.addEventListener('input', () => this.updateSelectedLabel(wrapper));
          wrapper.appendChild(input);
        } else {
          const grid = document.createElement('div');
          const images = options.some(option => option.image);
          grid.className = 'size-chooser__options' + (images ? ' size-chooser__options--images' : options.length <= 3 ? ' size-chooser__options--wide' : '');
          options.forEach((option, index) => {
            const id = `SizeChooser-${idPrefix}${group.key}-${index}`;
            const item = document.createElement('label');
            item.className = 'size-chooser__option';
            item.setAttribute('for', id);
            const box = option.image
              ? `<span class="size-chooser__option-box"><span class="size-chooser__option-head"><span class="size-chooser__option-label">${option.label}</span>${option.hint ? `<span class="size-chooser__option-hint">${option.hint}</span>` : ''}</span><img class="size-chooser__option-image" src="${option.image}" alt="" loading="lazy" width="415" height="224"></span>`
              : `<span class="size-chooser__option-box">${option.label}</span>`;
            item.innerHTML = `
              <input type="radio" id="${id}" name="size-chooser-${idPrefix}${group.key}" value="${option.value}" ${option.value === current ? 'checked' : ''}>
              ${box}
              ${option.hint && !option.image ? `<span class="size-chooser__option-hint">${option.hint}</span>` : ''}
            `;
            grid.appendChild(item);
          });
          wrapper.appendChild(grid);
        }

        if (group.type === 'backup') wrapper.dataset.of = group.of;
        wrapper.addEventListener('change', () => {
          this.updateSelectedLabel(wrapper);
          container.querySelectorAll(`[data-of="${group.key}"]`).forEach(backup => this.updateBackupOptions(backup));
        });
        container.appendChild(wrapper);
        this.updateSelectedLabel(wrapper);
      });
    }

    // "If that doesn't fit" answers: show the actual size one down / up from the first choice and turn off a
    // direction that has no size (no size down from XS)
    updateBackupOptions(wrapper) {
      const neighbours = this.backupNeighbours(wrapper.dataset.of, wrapper.closest('[data-size-chooser-find-groups]'));
      wrapper.querySelectorAll('input[type="radio"]').forEach(input => {
        const direction = input.value;
        if (direction === 'same') return;
        const size = neighbours[direction];
        const box = input.closest('label');
        let hint = box.querySelector('.size-chooser__option-hint');
        if (!hint) {
          hint = document.createElement('span');
          hint.className = 'size-chooser__option-hint';
          box.appendChild(hint);
        }
        hint.textContent = size || '';
        const available = Boolean(size) || !neighbours.primary;
        input.disabled = !available;
        box.classList.toggle('is-disabled', !available);
        if (!available && input.checked) {
          input.checked = false;
          wrapper.querySelector('input[value="same"]').checked = true;
        }
      });
      this.updateSelectedLabel(wrapper);
    }

    // The sizes one down and one up from the answer to the `of` question
    backupNeighbours(ofKey, container) {
      const group = (this.config.find || []).find(item => item.key === ofKey);
      const wrapper = container?.querySelector(`.size-chooser__group[data-key="${ofKey}"]`);
      const primary = wrapper ? this.valueOf(wrapper) : '';
      const values = group ? (group.options || []).map(String) : [];
      const index = values.indexOf(primary);
      return {
        primary,
        down: index > 0 ? values[index - 1] : '',
        up: index >= 0 && index < values.length - 1 ? values[index + 1] : ''
      };
    }

    // ----- dropdown (select-type groups, e.g. height) -----
    // A listbox instead of a native <select> so it matches the drawer and opens upward: these
    // groups sit near the bottom of the form, so the list only flips down when there's no room above.
    renderDropdown(group, options, current, idPrefix = '') {
      const id = `SizeChooser-${idPrefix}${group.key}`;
      const suffix = group.suffix || '';
      const placeholder = group.placeholder || `Select ${group.label.toLowerCase()}`;
      const dropdown = document.createElement('div');
      dropdown.className = 'size-chooser__dropdown';
      dropdown.dataset.dropdown = '';
      dropdown.dataset.placeholder = placeholder;
      dropdown.innerHTML = `
        <button type="button" class="size-chooser__dropdown-toggle" aria-haspopup="listbox" aria-expanded="false" aria-controls="${id}-listbox" aria-label="${group.question || group.label}">
          <span class="size-chooser__dropdown-value" data-dropdown-value></span>
        </button>
        <ul class="size-chooser__dropdown-list" id="${id}-listbox" role="listbox" tabindex="-1" aria-label="${group.label}" hidden>
          ${options
            .map(
              (option, index) => `
            <li class="size-chooser__dropdown-option" id="${id}-option-${index}" role="option" aria-selected="false" data-value="${option.value}" data-label="${option.label}${suffix.replace(/"/g, '&quot;')}">
              <span>${option.label}${suffix}</span>
              ${option.hint ? `<span class="size-chooser__dropdown-hint">${option.hint}</span>` : ''}
            </li>`
            )
            .join('')}
        </ul>
      `;

      const toggle = dropdown.querySelector('.size-chooser__dropdown-toggle');
      const list = dropdown.querySelector('[role="listbox"]');
      const items = [...list.querySelectorAll('[role="option"]')];

      toggle.addEventListener('click', () => (list.hidden ? this.openDropdown(dropdown) : this.closeDropdown(dropdown)));
      toggle.addEventListener('keydown', event => {
        if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
          event.preventDefault();
          this.openDropdown(dropdown);
        }
      });
      list.addEventListener('click', event => {
        const item = event.target.closest('[role="option"]');
        if (!item) return;
        this.setDropdownValue(dropdown, item.dataset.value, true);
        this.closeDropdown(dropdown, true);
      });
      list.addEventListener('mousemove', event => {
        const item = event.target.closest('[role="option"]');
        if (item) this.setActiveOption(dropdown, items.indexOf(item), false);
      });
      list.addEventListener('keydown', event => {
        const active = items.findIndex(item => item.classList.contains('is-active'));
        const move = {
          ArrowDown: Math.min(active + 1, items.length - 1),
          ArrowUp: Math.max(active - 1, 0),
          Home: 0,
          End: items.length - 1,
          PageDown: Math.min(active + 5, items.length - 1),
          PageUp: Math.max(active - 5, 0)
        };
        if (event.key in move) {
          event.preventDefault();
          this.setActiveOption(dropdown, move[event.key]);
        } else if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          if (items[active]) this.setDropdownValue(dropdown, items[active].dataset.value, true);
          this.closeDropdown(dropdown, true);
        } else if (event.key === 'Tab') {
          this.closeDropdown(dropdown);
        }
      });
      // Escape closes the list only, not the whole drawer (the drawer closes on keyup)
      dropdown.addEventListener('keyup', event => {
        if (event.key !== 'Escape' || list.hidden) return;
        event.stopPropagation();
        this.closeDropdown(dropdown, true);
      });

      this.setDropdownValue(dropdown, current, false);
      return dropdown;
    }

    openDropdown(dropdown) {
      if (this.openDropdownEl && this.openDropdownEl !== dropdown) this.closeDropdown(this.openDropdownEl);
      const toggle = dropdown.querySelector('.size-chooser__dropdown-toggle');
      const list = dropdown.querySelector('[role="listbox"]');
      const body = this.querySelector('.size-chooser__body') || this;

      list.hidden = false;
      list.style.maxHeight = '';
      const preferred = list.offsetHeight;
      const gap = 8;
      const toggleRect = toggle.getBoundingClientRect();
      const bodyRect = body.getBoundingClientRect();
      const above = toggleRect.top - bodyRect.top - gap;
      const below = bodyRect.bottom - toggleRect.bottom - gap;
      const up = above >= Math.min(preferred, 240) || above >= below;
      dropdown.classList.toggle('is-up', up);
      list.style.maxHeight = `${Math.max(Math.min(preferred, up ? above : below), 120)}px`;

      toggle.setAttribute('aria-expanded', 'true');
      dropdown.classList.add('is-open');
      this.openDropdownEl = dropdown;

      const items = [...list.querySelectorAll('[role="option"]')];
      const selected = items.findIndex(item => item.dataset.value === dropdown.dataset.value);
      this.setActiveOption(dropdown, selected >= 0 ? selected : 0, true, true);
      list.focus({ preventScroll: true });

      this._dropdownOutside = event => {
        if (!dropdown.contains(event.target)) this.closeDropdown(dropdown);
      };
      document.addEventListener('pointerdown', this._dropdownOutside, true);
    }

    closeDropdown(dropdown, refocus = false) {
      const list = dropdown.querySelector('[role="listbox"]');
      if (list.hidden) return;
      const toggle = dropdown.querySelector('.size-chooser__dropdown-toggle');
      list.hidden = true;
      list.removeAttribute('aria-activedescendant');
      toggle.setAttribute('aria-expanded', 'false');
      dropdown.classList.remove('is-open', 'is-up');
      if (this.openDropdownEl === dropdown) this.openDropdownEl = null;
      document.removeEventListener('pointerdown', this._dropdownOutside, true);
      if (refocus) toggle.focus();
    }

    // Highlight an option for keyboard/hover; centre it in the list when it was opened, otherwise keep it in view
    setActiveOption(dropdown, index, scroll = true, center = false) {
      const list = dropdown.querySelector('[role="listbox"]');
      const items = [...list.querySelectorAll('[role="option"]')];
      items.forEach((item, i) => item.classList.toggle('is-active', i === index));
      const item = items[index];
      if (!item) return;
      list.setAttribute('aria-activedescendant', item.id);
      if (!scroll) return;
      if (center) list.scrollTop = item.offsetTop - (list.clientHeight - item.offsetHeight) / 2;
      else if (item.offsetTop < list.scrollTop) list.scrollTop = item.offsetTop;
      else if (item.offsetTop + item.offsetHeight > list.scrollTop + list.clientHeight) list.scrollTop = item.offsetTop + item.offsetHeight - list.clientHeight;
    }

    setDropdownValue(dropdown, value, notify) {
      let label = '';
      dropdown.querySelectorAll('[role="option"]').forEach(item => {
        const selected = Boolean(value) && item.dataset.value === value;
        item.setAttribute('aria-selected', selected ? 'true' : 'false');
        if (selected) label = item.dataset.label;
      });
      dropdown.dataset.value = label ? value : '';
      dropdown.dataset.display = label;
      const valueEl = dropdown.querySelector('[data-dropdown-value]');
      valueEl.textContent = label || dropdown.dataset.placeholder;
      dropdown.classList.toggle('has-value', Boolean(label));
      if (notify) dropdown.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Number answers only count when they are inside the question's min/max
    valueOf(wrapper) {
      const dropdown = wrapper.querySelector('[data-dropdown]');
      if (dropdown) return dropdown.dataset.value || '';
      const number = wrapper.querySelector('input[type="number"]');
      if (number) {
        const value = parseFloat(number.value);
        const min = parseFloat(number.min);
        const max = parseFloat(number.max);
        return isNaN(value) || (!isNaN(min) && value < min) || (!isNaN(max) && value > max) ? '' : String(Math.round(value));
      }
      return wrapper.querySelector('input:checked')?.value || '';
    }

    displayValueOf(wrapper) {
      const dropdown = wrapper.querySelector('[data-dropdown]');
      if (dropdown) return dropdown.dataset.display || '';
      if (wrapper.querySelector('input[type="number"]')) return '';
      const checked = wrapper.querySelector('input:checked');
      const item = checked?.closest('label');
      const box = item && (item.querySelector('.size-chooser__option-label') || item.querySelector('.size-chooser__option-box'));
      return box ? box.textContent : '';
    }

    updateSelectedLabel(wrapper) {
      wrapper.querySelector('[data-selected]').textContent = this.displayValueOf(wrapper);
      if (this.valueOf(wrapper)) wrapper.classList.remove('is-invalid');
      if (!wrapper.parentElement?.querySelector('.is-invalid')) this.errorEl.hidden = true;
    }

    // Highlight the sections still missing a choice and bring the first one into view
    showValidation(root = this.groupsEl) {
      let first = null;
      root.querySelectorAll('.size-chooser__group').forEach(wrapper => {
        const missing = !this.valueOf(wrapper);
        wrapper.classList.toggle('is-invalid', missing);
        if (missing && !first) first = wrapper;
      });
      this.errorEl.hidden = !first;
      first?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }

    getSelection(root = this.groupsEl) {
      const selection = {};
      let complete = true;
      root.querySelectorAll('.size-chooser__group').forEach(wrapper => {
        const value = this.valueOf(wrapper);
        if (value) selection[wrapper.dataset.key] = value;
        else complete = false;
      });
      return { selection, complete };
    }

    resetSelection() {
      this.groupsEl.querySelectorAll('input:checked').forEach(input => {
        input.checked = false;
      });
      this.groupsEl.querySelectorAll('[data-dropdown]').forEach(dropdown => {
        this.closeDropdown(dropdown);
        this.setDropdownValue(dropdown, '', false);
      });
      this.groupsEl.querySelectorAll('.size-chooser__group').forEach(wrapper => {
        wrapper.classList.remove('is-invalid');
        this.updateSelectedLabel(wrapper);
      });
      this.errorEl.hidden = true;
    }

    handleError(error) {
      console.error('Size chooser', error);
      this.setBusy(null);
      if (error && error.code === 'LOGIN_REQUIRED') {
        this.setStatus('Sizes are saved to your account so we can build your pattern.');
        this.showLogin(true);
      } else {
        this.setStatus('Something went wrong. Please try again.');
      }
    }

    async clear() {
      if (this.busy) return;
      this.resetSelection();
      if (typeof this.callbacks?.onClear !== 'function') return;

      this.setBusy(this.clearButton);
      this.setStatus('Removing your saved size…');
      try {
        await this.callbacks.onClear({ modes: this.modesForTypes(this.productTypes) });
        this.setBusy(null);
        this.close();
      } catch (error) {
        this.handleError(error);
      }
    }

    // Find your size: score the survey's step 1 answers. A match fills in the standard size form and saves it;
    // no match sends the customer on to the custom survey with these answers filled in.
    async findSize() {
      if (this.busy) return;
      const { selection: raw, complete } = this.getSelection(this.findGroupsEl);
      if (!complete) {
        this.showValidation(this.findGroupsEl);
        return;
      }
      const answers = this.surveyAnswers(raw);
      const result = window.SimpleSize?.find ? window.SimpleSize.find(this.modesForTypes(this.productTypes), answers) : { match: false };

      if (result.match) {
        const { selection: current } = this.getSelection();
        const selection = { ...current, ...result.selection };
        this.renderGroups(this.productTypes, selection);
        this.showStep('standard');
        this.showMatch(`We matched you to: ${this.describeSelection(selection)}.`);
        await this.save();
        return;
      }

      this.setBusy(this.findButton);
      this.setStatus("We couldn't match you to a standard size, so we'll take you to the custom size survey with your answers filled in.");
      await new Promise(resolve => setTimeout(resolve, 2000));
      this.setBusy(null);
      this.startCustom(answers);
    }

    // Find your size answers in the survey's formData shape ("Size down / up" become the actual sizes)
    surveyAnswers(raw) {
      const answers = { ...raw };
      (this.config.find || [])
        .filter(group => group.type === 'backup')
        .forEach(group => {
          const neighbours = this.backupNeighbours(group.of, this.findGroupsEl);
          answers[group.key] = neighbours[raw[group.key]] || '';
        });
      return answers;
    }

    // "Jacket 40 · Waist 32 · Height 5'10"" for the groups currently shown
    describeSelection(selection) {
      return (this.currentGroups || [])
        .filter(group => selection[group.key] && group.key !== 'fit')
        .map(group => {
          const value = selection[group.key];
          const label = (group.labels && group.labels[value]) || value;
          return `${group.label} ${label}${group.suffix || ''}`;
        })
        .join(' · ');
    }

    // Fill the MCC survey's step 1 with Find your size answers once it is on screen. The survey is a React
    // form, so values go through the native setters and an input/change event for React to pick up.
    prefillSurvey(answers) {
      const started = Date.now();
      const labelFor = (root, pattern) => [...root.querySelectorAll('.field__label')].find(label => pattern.test(label.textContent.trim()));
      const fieldFor = (root, pattern) => labelFor(root, pattern)?.closest('.form-group')?.querySelector('input, select') || null;
      const setField = (field, value) => {
        if (!field || value == null || value === '') return;
        const proto = field.tagName === 'SELECT' ? HTMLSelectElement.prototype : HTMLInputElement.prototype;
        Object.getOwnPropertyDescriptor(proto, 'value').set.call(field, String(value));
        field.dispatchEvent(new Event(field.tagName === 'SELECT' ? 'change' : 'input', { bubbles: true }));
      };

      const poll = () => {
        const weight = [...document.querySelectorAll('.field__label')].find(label => /^weight/i.test(label.textContent.trim()) && label.offsetParent);
        let root = weight?.parentElement;
        while (root && !labelFor(root, /try on first \(t-shirt\)/i)) root = root.parentElement;
        if (!root) {
          if (Date.now() - started < 20000) setTimeout(poll, 250);
          return;
        }
        setField(fieldFor(root, /^height$/i), answers.height);
        setField(fieldFor(root, /^weight/i), answers.weight);
        setField(fieldFor(root, /^age$/i), answers.age);
        setField(fieldFor(root, /try on first \(t-shirt\)/i), answers.upperSize);
        setField(fieldFor(root, /pants size do you try on first/i), answers.jeanSize);
        // Picking a first size resets and rebuilds the "try next" lists, so fill those after React re-renders
        setTimeout(() => {
          setField(fieldFor(root, /what do you try next/i), answers.backupSize);
          setField(fieldFor(root, /what size do you try next/i), answers.backupJeanSize);
        }, 150);
      };
      poll();
    }

    async save() {
      if (this.busy) return;
      const { selection, complete } = this.getSelection();
      if (!complete) {
        this.showValidation();
        return;
      }
      this.setBusy(this.saveButton);
      this.setStatus('Building your measurements…');
      this.showLogin(false);
      try {
        await this.callbacks?.onStandard?.({ selection, modes: this.modesForTypes(this.productTypes) });
        this.track('saved', { found: Boolean(this.matchEl && !this.matchEl.hidden) });
        this.setBusy(null);
        this.close();
      } catch (error) {
        this.handleError(error);
      }
    }
  }

  customElements.define('size-chooser', SizeChooser);
  window.SizeChooser = SizeChooser;
}
